import React, { useEffect, useState } from "react";
import { Person, FunctionOption, FunctionWithZones } from "./types";
import "./../Event/AdminEvent.css";

interface Props {
    persons: Person[];
    onEdit: (personId: number) => void;
    onDelete: (personId: number) => void;
    tableId?: string;
    onSelectPersons?: (selected: Person[]) => void;
    printedStatus: (personId: number, eventId?: number | null) => boolean;
    selectedEventId?: number | null;
    allFunctions: FunctionWithZones[];
}

const PersonsList: React.FC<Props> = ({
    persons,
    onEdit,
    onDelete,
    tableId,
    onSelectPersons,
    printedStatus,
    selectedEventId,
    allFunctions,
}) => {
    const [selectedIds, setSelectedIds] = useState<number[]>([]);

    // Reset selection når data ændrer sig (fx ny side / nye filtre)
    useEffect(() => {
        setSelectedIds([]);
        onSelectPersons?.([]);
    }, [persons, onSelectPersons]);

    const handleSelect = (personId: number, checked: boolean) => {
        const updated = checked
            ? [...selectedIds, personId]
            : selectedIds.filter((id) => id !== personId);

        setSelectedIds(updated);
        onSelectPersons?.(
            persons.filter((p) => updated.includes(p.personId))
        );
    };

    const handleSelectAll = (checked: boolean) => {
        const allIds = persons.map((p) => p.personId);

        const updated = checked
            ? Array.from(new Set([...selectedIds, ...allIds]))
            : selectedIds.filter((id) => !allIds.includes(id));

        setSelectedIds(updated);
        onSelectPersons?.(
            persons.filter((p) => updated.includes(p.personId))
        );
    };

    const allFunctionsMap = React.useMemo(() => {
        return new Map<number, FunctionOption>(
            allFunctions.map(f => [Number(f.functionId), f])
        );
    }, [allFunctions]);

    return (
        <div className="group-table-container">
            <div className="nice-table-wrap">
                <table
                    id={tableId}
                    className="event-table event-table-modern nice-table"
                >
                    <thead>
                        <tr>
                            <th style={{ width: 40 }}>
                                <input
                                    type="checkbox"
                                    checked={
                                        persons.length > 0 &&
                                        persons.every((p) =>
                                            selectedIds.includes(p.personId)
                                        )
                                    }
                                    onChange={(e) =>
                                        handleSelectAll(e.target.checked)
                                    }
                                />
                            </th>
                            <th>ID</th>
                            <th>Navn</th>
                            <th>Land</th>
                            <th>Title</th>
                            <th>Company</th>
                            <th>Profilbillede</th>
                            <th>Print Status</th>
                            <th>Events</th>
                            <th>Funktioner</th>
                            <th className="col-actions">Handlinger</th>
                        </tr>
                    </thead>

                    <tbody>
                        {persons.length > 0 ? (
                            persons.map((p) => (
                                <tr key={p.personId}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedIds.includes(p.personId)}
                                            onChange={(e) =>
                                                handleSelect(
                                                    p.personId,
                                                    e.target.checked
                                                )
                                            }
                                        />
                                    </td>

                                    <td className="col-id">{p.personId}</td>

                                    <td className="col-name">
                                        {p.firstName} {p.lastName}
                                    </td>
                                    <td>{p.country ?? "-"}</td>
                                    <td>{p.title ?? "-"}</td>
                                    <td>{p.company ?? "-"}</td>

                                    {/* Profilbillede */}
                                    <td>
                                        {p.hasProfileImage ? (
                                            <span style={{ color: "green" }}>✔</span>
                                        ) : (
                                            <span style={{ color: "red" }}>❌</span>
                                        )}
                                    </td>

                                    {/* Print status */}
                                    <td>
                                        {printedStatus(p.personId, selectedEventId) ? (
                                            <span style={{ color: "green" }}>✔</span>
                                        ) : (
                                            <span style={{ color: "red" }}>❌</span>
                                        )}
                                    </td>
                                    {/* Events */}
                                    <td>
                                        {p.events?.length ? (
                                            <div className="chip-row">
                                                {p.events
                                                    .filter((e) => {
                                                        if (!selectedEventId) return true;
                                                        return Number(e.eventID) === Number(selectedEventId);
                                                    })
                                                    .map((e) => (
                                                        <span
                                                            key={`${p.personId}-${e.eventID}`}
                                                            className="chip"
                                                        >
                                                            <span className="chip-text">
                                                                {e.eventName}
                                                            </span>
                                                        </span>
                                                    ))}
                                            </div>
                                        ) : (
                                            <span className="muted">-</span>
                                        )}
                                    </td>

                                    {/* Funktioner */}
                                    <td>
                                        {p.functions
                                            ?.filter((fn) => {
                                                if (!selectedEventId) return true;
                                                return Number(fn.eventId) === Number(selectedEventId);
                                            })
                                            .map((fn) => (
                                                <span
                                                    key={`${p.personId}-${fn.functionId}`}
                                                    className="chip"
                                                >
                                                    <span className="chip-text">
                                                        {fn.functionName}
                                                    </span>
                                                </span>
                                            ))}
                                    </td>

                                    <td>
                                        <div className="table-buttons">
                                            <button
                                                className="btn edit-btn"
                                                onClick={() =>
                                                    onEdit(p.personId)
                                                }
                                            >
                                                Rediger
                                            </button>

                                            <button
                                                className="btn btn--delete"
                                                onClick={() => {
                                                    const confirmed =
                                                        window.confirm(
                                                            "Slet denne person?"
                                                        );
                                                    if (!confirmed) return;
                                                    onDelete(p.personId);
                                                }}
                                            >
                                                Slet
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={11} className="empty">
                                    Ingen personer fundet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default PersonsList;